import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errText, img, money } from "../../lib/api";
import { STATUS_LABELS } from "../../lib/i18n";
import { Btn, Card, PageHeader } from "../../components/admin/ui";
import { Input } from "../../components/ui/input";
import { StatusBadge } from "./Orders";

const Row = ({ l, v, b }) => <div className={`flex justify-between py-1 text-sm ${b ? "border-t pt-2 font-bold" : ""}`}><span className="text-gray-500">{l}</span><span>{v}</span></div>;

export default function OrderDetail() {
  const { number } = useParams();
  const [o, setO] = useState(null);
  const [note, setNote] = useState("");
  const load = () => api.get(`/admin/orders/${number}`).then((r) => setO(r.data));
  useEffect(() => { load(); }, [number]); // eslint-disable-line

  const change = async (status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${number}/status`, { status, note });
      toast.success(`تم تحديث الحالة: ${STATUS_LABELS[status].ar}${data.emailed ? " — تم إرسال بريد للعميلة" : ""}`);
      setNote("");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!o) return null;
  const a = o.address;
  return (
    <div data-testid="admin-order-detail">
      <PageHeader title={`الطلب ${o.order_number}`} desc={new Date(o.created_at).toLocaleString("ar-SA")}>
        <Link to="/admin/orders"><Btn variant="light">رجوع</Btn></Link>
        <Btn variant="light" onClick={() => window.print()}>طباعة</Btn>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="المنتجات">
            {o.items.map((i, k) => (
              <div key={k} className="flex items-center gap-3 border-b border-gray-100 py-3 text-sm last:border-0">
                <img src={img(i.image)} alt="" className="h-14 w-11 rounded object-cover" />
                <div className="flex-1"><p className="font-medium">{i.name_ar}</p><p className="text-xs text-gray-500">{[i.size, i.color].filter(Boolean).join(" / ")} · <span className="font-mono">{i.sku}</span></p></div>
                <span>{i.qty} × {money(i.price)}</span><span className="w-24 text-left font-medium">{money(i.line_total)} ر.س</span>
              </div>
            ))}
            <div className="mt-3 max-w-xs">
              <Row l="المجموع الفرعي" v={money(o.subtotal)} />
              {o.discount > 0 && <Row l={`الخصم (${o.coupon_code})`} v={`- ${money(o.discount)}`} />}
              <Row l={`الشحن${o.zone ? ` — ${o.zone.name_ar}` : ""}`} v={money(o.shipping)} />
              {o.cod_fee > 0 && <Row l="رسوم الدفع عند الاستلام" v={money(o.cod_fee)} />}
              <Row l={o.tax_inclusive ? "الضريبة (مشمولة)" : "الضريبة"} v={money(o.tax)} />
              <Row l="الإجمالي" v={`${money(o.total)} ر.س`} b />
            </div>
          </Card>
          <Card title="سجل الحالات">
            {o.status_history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5 text-sm"><StatusBadge s={h.status} /><span className="text-xs text-gray-500">{new Date(h.at).toLocaleString("ar-SA")}</span>{h.note && <span className="text-gray-600">— {h.note}</span>}</div>
            ))}
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="الحالة">
            <div className="mb-3 flex items-center gap-2"><span data-testid="admin-order-status"><StatusBadge s={o.status} /></span><span className="text-xs text-gray-500">الدفع: {o.payment_method === "online" ? "إلكتروني (ميسر)" : "عند الاستلام"} ({o.payment_status === "paid" ? "مدفوع" : o.payment_status === "refunded" ? "مسترد" : "غير مدفوع"})</span></div>
            {o.allowed_next.length > 0 ? (
              <>
                <Input data-testid="status-note-input" placeholder={o.allowed_next.includes("shipped") ? "ملاحظة / رقم التتبع (تُرسل للعميلة مع بريد الشحن)" : "ملاحظة (اختياري)"} value={note} onChange={(e) => setNote(e.target.value)} className="mb-2" />
                <div className="flex flex-wrap gap-2">
                  {o.allowed_next.map((s) => (
                    <Btn key={s} data-testid={`status-action-${s}`} variant={["cancelled", "refunded"].includes(s) ? "danger" : "dark"} onClick={() => change(s)}>{STATUS_LABELS[s].ar}</Btn>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">عند الإلغاء أو الاسترداد تتم إعادة الكميات إلى المخزون تلقائيًا.</p>
                {o.allowed_next.includes("shipped") && <p data-testid="shipped-email-hint" className="mt-1 text-xs text-gray-500">{o.customer.email ? "عند اختيار «تم الشحن» يُرسل بريد تلقائي للعميلة برقم الطلب." : "لا يوجد بريد للعميلة، لن يُرسل بريد الشحن."}</p>}
              </>
            ) : <p className="text-sm text-gray-500">لا توجد إجراءات متاحة</p>}
          </Card>
          <Card title="العميلة">
            <p className="font-medium">{o.customer.name}</p>
            <a href={`tel:${o.customer.phone}`} className="block text-sm text-gray-600" dir="ltr">{o.customer.phone}</a>
            <a href={`https://wa.me/966${o.customer.phone.slice(1)}`} target="_blank" rel="noreferrer" className="text-xs text-green-700">واتساب</a>
            {o.customer.email && <p className="text-sm text-gray-600">{o.customer.email}</p>}
          </Card>
          <Card title="عنوان التوصيل">
            <p className="text-sm leading-7">{a.city} — {a.district}<br />{a.street}{a.building_no && `، مبنى ${a.building_no}`}{a.additional_no && `، إضافي ${a.additional_no}`}<br />
              {a.postal_code && `الرمز البريدي ${a.postal_code}`} {a.short_address && <span className="font-mono">· {a.short_address}</span>}</p>
            {a.notes && <p className="mt-2 rounded bg-gray-50 p-2 text-xs">{a.notes}</p>}
          </Card>
        </div>
      </div>
    </div>
  );
}
